/**
 * API Request Layer for Sessions Tab in TPRADashboard
 *
 * Fetches ALL MULTI_FILE_VENDOR_ASSESSMENT sessions at once (page_size: 1000)
 * for client-side filtering and pagination.
 * NO server-side search/pagination - backend doesn't support it yet.
 */

import { getPaginatedAgentSessions } from "@/modules/sessions/requests";
import { AgentSessionType, AgentSessionStatus } from "@/modules/sessions/types";
import { useQuery } from "@tanstack/react-query";
import { QueryKeys } from "@/shared/constants/QueryKeys";
import type { SessionDetails } from "../types/sessions";
import { getSessionStatusLevel } from "@/modules/sessions/constants/columns";

/**
 * Transform API response to UI format (without member name resolution)
 *
 * Maps raw API session data to UI-compatible format with:
 * - Computed status level for sorting
 * - Final report availability flag
 * Note: Member name resolution happens in the component layer via useEffect
 */
function transformSessionsData(response: any): SessionDetails[] {
  const sessionsArr = Array.isArray(response.data.sessions) ? response.data.sessions : [];

  return sessionsArr.map((item: any) => {
    // Check if session is ready for download (same logic as Sessions module)
    // Session is ready if status is NOT one of the in-progress statuses
    const isReadyForDownload = ![
      AgentSessionStatus.PENDING,
      AgentSessionStatus.QUEUED,
      AgentSessionStatus.IN_PROGRESS,
      AgentSessionStatus.UNDER_REASSESSMENT,
      AgentSessionStatus.FORM_SENT,
      AgentSessionStatus.VENDOR_INPUT_NEEDED,
      AgentSessionStatus.EXTRACT_CONTROLS,
      AgentSessionStatus.REFINE_IN_PROGRESS,
    ].includes(item.status);

    return {
      id: item.id,
      name: item.name,
      vendor_name: item.multi_file_vendor_assessment?.vendor_name,
      product_name: item.multi_file_vendor_assessment?.product_name,
      status: item.status,
      status_level: getSessionStatusLevel(item.status),
      updated_at: item.updated_at,
      // API returns created_by as the member ID (same as Sessions module line 203)
      created_by_id: item.created_by,
      created_by: "", // Will be resolved in component via useEffect
      permissions: item.permissions,
      // Download is available if session is ready AND final report is available
      final_report_available: isReadyForDownload && (item.final_report_available ?? false),
    };
  });
}

/**
 * Async function to fetch ALL sessions at once
 *
 * Fetches all MULTI_FILE_VENDOR_ASSESSMENT sessions with page_size: 1000
 * for client-side filtering/pagination. Backend doesn't support search yet.
 *
 * @param signal - Optional abort signal for request cancellation
 * @returns Promise with array of transformed session details
 */
export async function getAllSessions(signal?: AbortSignal): Promise<SessionDetails[]> {
  const response = await getPaginatedAgentSessions({
    page_number: 1,
    page_size: 1000,  // Fetch ALL sessions at once (client-side filtering)
    filters: {
      session_type: [AgentSessionType.MULTI_FILE_VENDOR_ASSESSMENT],
    },
    signal,
  });

  return transformSessionsData(response);
}

/**
 * React Query hook for fetching all sessions
 *
 * Fetches all sessions at once for client-side filtering and pagination.
 * NO query params - simple cache key for optimal performance.
 *
 * @returns React Query result with all sessions data
 */
export function useGetAllSessions() {
  return useQuery({
    queryKey: [QueryKeys.TPRASessions],  // Simple key (no params)
    queryFn: ({ signal }) => getAllSessions(signal),
    staleTime: 0,  // Always refetch for fresh data
    gcTime: 10 * 60 * 1000,    // 10 minutes (cache)
    retry: 2,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 5000),
    refetchOnWindowFocus: true,  // Refetch when returning to browser tab
    refetchOnMount: true,
    refetchOnReconnect: true,
  });
}
